import React from "react";
import {
    Select,
    Input,
    InputNumber,
    Radio,
    TimePicker,
    DatePicker,
    AutoComplete,
    Form,
    Table as AntTable,
    Card,
    Checkbox,
    Button,
    TreeSelect,
    Icon
} from "antd";
import { Table } from "components";
import { ObjectText, ObjectSelect, FileUploader, MultiTableSelect, TilesView, DraggableUploader, NumberSelect, DateSelect } from "components/form";
import noop from "lodash/noop";
import { INPUT_TYPES } from "../constants";
import DaysCalculator from "components/days-calculator";
import { OrderedMap, List } from "immutable";

const { SHOW_PARENT } = TreeSelect;
const SelectOption = Select.Option;
const SelectOptGroup = Select.OptGroup;
const RadioGroup = Radio.Group;
const RadioButton = Radio.Button;
const MonthPicker = DatePicker.MonthPicker;
const dateFormat = "YYYY-MM-DD";
const timeFormat = "HH:mm";
const BASE_FORM_LAYOUT = {
    labelCol: { span: 24 },
    wrapperCol: { span: 24 }
};

const FormItem = Form.Item;


export const onCellGenerator = (handleSave, _, bindIndex=false) => (record, rowIndex) => ({
    record,
    editable: true,
    dataIndex: _.dataIndex,
    title: record.header || _.title,
    handleSave: bindIndex ? handleSave.bind(null, rowIndex) : handleSave,
    rules: record.rules || _.rules,
    fieldType: record.fieldType || _.fieldType,
    filesMultiple: _.filesMultiple ? _.filesMultiple : false,
    options: record.options || _.options,
    getFileUploadParams: _.getFileUploadParams,
    uploadTarget: _.uploadTarget,
    viewable: _.viewable,
    max: record.max
});

export function formHasErrors() {
    const fieldsError = this && this.getFieldsError();
    if (fieldsError) {
        return Object.keys(fieldsError).some(field => fieldsError[field]);
    }
    return false;
}

export function getErrorOnField(fieldName) {
    const { isFieldTouched, getFieldError } = this;
    return isFieldTouched(fieldName) && getFieldError(fieldName);
}


const VALIDATE_STATUS = {
    ERROR: "error",
    SUCCESS: "success",
    VALIDATING: "validating"
};

export function getValidateStatus(fieldName) {
    const {
        isFieldValidating,
        isFieldTouched,
        getFieldError,
        getFieldValue
    } = this;
    if (isFieldValidating(fieldName)) {
        return VALIDATE_STATUS.VALIDATING;
    }
    if (getFieldError(fieldName)) {
        return VALIDATE_STATUS.ERROR;
    }
    if (isFieldTouched(fieldName) && getFieldValue(fieldName)) {
        return VALIDATE_STATUS.SUCCESS;
    }
    return null;
}

export function getValidateMessage(fieldName, field={}) {
    const {
        getFieldError,
        getFieldValue
    } = this;
    // if (isFieldValidating(fieldName)) {
    //     return VALIDATE_STATUS.VALIDATING;
    // }
    if (getFieldError(fieldName)) {
        return getFieldError(fieldName);
    }
    if (field.showSuccessValidation && getFieldValue(fieldName)) {
        return VALIDATE_STATUS.SUCCESS;
    }
    return null;
}

export function submitForm(submitValues, ev) {
    ev.preventDefault();
    this.validateFields((err, values) => {
        if (!err) {
            return submitValues(values);
        }
    });
}

export function getFieldType(field, fieldDecorator) {
    let addBefore = null,
        addAfter = null;
    if (field.grouped && field.addBefore) {
        const addBeforeField = field.addBefore;
        addBefore = fieldDecorator(field.addBefore.name, {
            initialValue: addBeforeField.defaultValue
        })(getFieldType(addBeforeField));
    }
    if (field.grouped && field.addAfter) {
        const addAfterField = field.addAfter;
        addBefore = fieldDecorator(field.addAfter.name, {
            initialValue: addAfterField.defaultValue
        })(getFieldType(addAfterField));
    }
    switch (field.type) {
    case INPUT_TYPES.DRAGGABLEUPLOADER:
        return React.createElement(DraggableUploader, {
            multiple: field.multiple,
            action: field.action,
            accept: field.accept,
            beforeUpload: field.beforeUpload,
            onRemove: field.onRemove,
            title: field.title,
            subtitle: field.subtitle,
            onFileUploadComplete: () => {}
        });
    case INPUT_TYPES.FILE:
        return React.createElement(FileUploader, {
            mode: field.mode,
            btnLabel: field.btnLabel,
            accept: field.accept,
            disabled: field.disabled,
            showUploadList: field.showUploadList,
            fileList: field.fileList || [],
            multiple: field.multiple
        });
    case INPUT_TYPES.SEARCH:
        return React.createElement(Input.Search, {
            placeholder: field.placeholder,
            type: field.type,
            onSearch: field.onSearch,
            size: field.size || "default"
        });
    case INPUT_TYPES.AUTOCOMPLETE:
        return React.createElement(AutoComplete, {
            dataSource: field.options,
            placeholder: field.placeholder,
            filterOption: field.filterOption || true,
            onSearch: field.onSearch || noop,
            size: field.size || "default"
        });
    case INPUT_TYPES.CHECKBOX:
        return React.createElement(
            Checkbox,
            {
                checked: field.checked,
                onChange: field.onChange,
                defaultChecked: field.defaultChecked
            }
        );
    case INPUT_TYPES.SELECT: {
        const fieldOptions= field.options && field.options.map((option,index) => {
            if(option.length>=0) {
                return React.createElement(
                    SelectOptGroup,
                    {
                        key: index,
                        label: index,
                    },                    
                    option.map(_ =>
                        React.createElement(
                            SelectOption,
                            {
                                key: _.label,
                                value: _.value,
                                ...(_.rest||{})
                            },
                            _.label
                        )
                    )
                );
            } else {
                return React.createElement(
                    SelectOption,
                    {
                        key: option.label,
                        value: option.value,
                        label: option.label,
                        ...(option.rest||{})
                    },
                    option.label
                );
            }
        });
        return React.createElement(
            Select,
            {
                placeholder: field.placeholder,
                mode: field.mode||"default",
                showSearch: field.showSearch||false,
                onSelect: field.onSelect,
                onSearch: field.onSearch,
                onChange:  field.onChange,
                style: field.style||{
                    minWidth: 200
                },
                allowClear: field.allowClear ? false : true,
                dropdownMenuStyle: field.dropdownMenuStyle,
                disabled: field.disabled,
                size: field.size||"default",
                optionLabelProp: field.optionLabelProp||"label",
                filterOption: field.filterOption,
                dropdownMatchSelectWidth: field.dropdownMatchSelectWidth
            },
            fieldOptions && fieldOptions.size >= 0 ? fieldOptions.toList() : fieldOptions
        );
    }
    case INPUT_TYPES.TREE_SELECT: 
        return (
            <TreeSelect
                treeData={field.treeData}
                // value={field.value}
                onChange={field.onChange}
                treeCheckable={true}
                showCheckedStrategy={SHOW_PARENT}
                searchPlaceholder={field.searchPlaceholder}
                style={{
                    width: "100%"
                }}
            />
        );
    case INPUT_TYPES.RADIO:
        return React.createElement(
            RadioGroup,
            {
                disabled : field.disabled || false,
                size: field.size || "default",
                onChange: field.onChange
            },
            field.options.map(_ =>
                React.createElement(
                    RadioButton,
                    {
                        key: _.label,
                        value: _.value
                    },
                    _.label
                )
            )
        );
    case INPUT_TYPES.BUTTON:
        return (
            <Button disabled={field.disabled || false} onClick={field.onClick} type={field.btnType || "link"} icon={field.icon}>
                {field.placeholder}
            </Button>
        );
    case INPUT_TYPES.NUMBER:
        return React.createElement(InputNumber, {
            addonBefore: addBefore,
            addonAfter: addAfter,
            className: field.className,
            placeholder: field.placeholder,
            onChange: field.onChange || noop,
            prefix: field.prefix,
            style: field.style,
            disabled: field.disabled,
            size: field.size || "100"
        });
    case INPUT_TYPES.FLOAT:
        return React.createElement(InputNumber, {
            addonBefore: addBefore,
            addonAfter: addAfter,
            className: field.className,
            placeholder: field.placeholder,
            onChange: field.onChange || noop,
            prefix: field.prefix,
            style: field.style,
            disabled: field.disabled,
            size: field.size || "100",
            step: 0.01
        });
    case INPUT_TYPES.OBJECT_TEXT:
        return (
            <ObjectText
                addonBefore={addBefore}
                addonAfter={addAfter}
                placeholder={field.placeholder}
                prefix={field.prefix}
                disabled={field.disabled}
                onFocus={field.onFocus}
                onChange={field.onChange}
                inputForm={field.inputForm}
                inputformprops={field.inputformprops}
                getValueFromObject={field.getValueFromObject}
                size={field.size}
            />
        );
    case INPUT_TYPES.OBJECT_SELECT:
        return (
            <ObjectSelect
                placeholder={field.placeholder}
                mode={field.mode}
                onSelect={field.onSelect}
                onChange={field.onChange}
                onSearch={field.onSearch || noop}
                disabled={field.disabled}
                showSearch={field.showSearch}
                defaultValue={field.defaultValue}
                showArrow={true}
                onFocus={field.onFocus}
                filterOption={field.filterOption}
                style={field.style}
                loading={field.loading}
                labelProp={field.labelProp}
                notFoundContent={field.notFoundContent || "Not Found"}
                dropdownMenuStyle={field.dropdownMenuStyle}
                dataSource={field.options}
                parentClass={field.parentClass}
                autoClearSearchValue={field.autoClearSearchValue}
                getOptionsOnFocus={field.getOptionsOnFocus}
                getOptionsInApiResponse={field.getOptionsInApiResponse}
                formatData={field.formatData}
            />
        );
    case INPUT_TYPES.MULTI_TABLE_SELECT:
        return (
            <MultiTableSelect
                placeholder={field.placeholder}
                mode={field.mode}
                onSelect={field.onSelect}
                onChange={field.onChange}
                onSearch={field.onSearch || noop}
                disabled={field.disabled}
                showSearch={field.showSearch}
                showArrow={true}
                style={field.style}
                loading={field.loading}
                labelProp={field.labelProp}
                dropdownMenuStyle={field.dropdownMenuStyle}
                dataSource={field.options}
                getOptionsOnFocus={field.getOptionsOnFocus}
                valueFunc={field.valueFunc}
                keyFunc={field.keyFunc}
                labelFunc={field.labelFunc}
                tableData={field.tableData}
            />
        );
    case INPUT_TYPES.TABLE:
        return(
            <Card
                className={"padless-card p-10"}
                extra={field.extra}
                title={field.title}
            >
                <AntTable
                    style={field.style}
                    dataSource={field.dataSource}
                    columns={field.columns}
                    pagination={field.pagination}
                    loading={field.loading}
                    bordered={field.bordered}
                />
            </Card>
        );
    case INPUT_TYPES.TABLE_EDITOR:
        return (
            <Table
                columns={new OrderedMap(field.columns).map(_ => {
                    let row = _;
                    row.onCell = onCellGenerator(field.handleSave, _, true);
                    return row;
                })}
                data={field.value ? new List(field.value) : new List([])}
                showTopSection={false}
                pagination={false}
                noRowSelection={true}
                showFooter={false}
                bordered={true}
                editable={true}
                type={"inner"}
                tablePagination={false}
            />
        );
    case INPUT_TYPES.TILES:
        return (
            <TilesView
                title={field.label}
                images={field.data || field.defaultValue}
                onAddImage={field.onAddImage}
                onChange={field.onChange}
                extra={field.extra || []}
                onFileUploadComplete={field.onFileUploadComplete}
            />
        );
    case INPUT_TYPES.EMAIL:
        return React.createElement(Input, {
            addonBefore: addBefore,
            addonAfter: addAfter,
            placeholder: field.placeholder,
            onChange: field.onChange || noop,
            prefix: field.prefix,
            disabled: field.disabled,
            size: field.size || "default"
        });

    case INPUT_TYPES.DATE:
        return React.createElement(DatePicker, {
            placeholder: field.placeholder,
            prefix: field.prefix,
            format: dateFormat,
            disabledDate: field.disabledDate,
            disabled: field.disabled,
            size: field.size || "default",
            ...(field.mode ? { mode: field.mode } : {})
        });
    case INPUT_TYPES.DATE_RANGE:
        return (
            <DateSelect
                addonBefore={addBefore}
                addonAfter={addAfter}
                placeholder={field.placeholder}
                prefix={field.prefix}
                format={dateFormat}
                disabledDate={field.disabledDate}
            />
        );
    case INPUT_TYPES.TIME:
        return React.createElement(TimePicker, {
            placeholder: field.placeholder,
            format: timeFormat,
            disabledDate: field.disabledDate,
            disabledHours: field.disabledHours,
            disabledMinutes: field.disabledMinutes,
            minuteStep: field.minuteStep,
            disabled: field.disabled,
            size: field.size || "default",
            ...(field.mode ? { mode: field.mode } : {})
        });
    case INPUT_TYPES.DATE_MONTH:
        return React.createElement(MonthPicker, {
            placeholder: field.placeholder,
            prefix: field.prefix,
            disabled: field.disabled,
            disabledDate: field.disabledDate || false,
            size: field.size || "default",
            ...(field.mode ? { mode: field.mode } : {})
        });
    case INPUT_TYPES.PASSWORD:
        return React.createElement(Input, {
            addonBefore: addBefore,
            addonAfter: addAfter,
            placeholder: field.placeholder,
            onChange: field.onChange || noop,
            prefix: field.prefix,
            disabled: field.disabled,
            autoComplete:field.autocomplete,
            size: field.size || "default",
            type: "password"
        });
    case INPUT_TYPES.TEXTAREA:
        return React.createElement(Input.TextArea, {
            placeholder: field.placeholder,
            prefix: field.prefix,
            onChange: field.onChange || noop,
            disabled: field.disabled,
            size: field.size || "default",
            autosize: { minRows: 2, maxRows: 4 }
        });
    case INPUT_TYPES.DAYS_CALCULATOR:
        return (
            <DaysCalculator
                days={field.defaultValue}
                isFieldChecked={field.isFieldChecked}
                updateDays={field.onChange}
                checked={field.checked}
            />
        );
    case INPUT_TYPES.NUMBER_RANGE:
        return (
            <NumberSelect
                addonBefore={addBefore}
                addonAfter={addAfter}
                placeholder={field.placeholder}
                prefix={field.prefix}
                disabled={field.disabled}
                size={"100"}
                getValueFromObject={field.getValueFromObject}
            />
        );
    case INPUT_TYPES.DISPLAY_NONE:
        return React.createElement(Input, {
            type: "text",
            style: {
                width: 0,
                height: 0,
                display: "none"
            }
        });
    default:
        return React.createElement(Input, {
            onCopy :  field.disableFieldCopy,
            autoComplete:field.autocomplete,
            onPaste :  field.disableFieldPaste,
            addonBefore: addBefore,
            addonAfter: addAfter,
            placeholder: field.placeholder,
            onChange: field.onChange || noop,
            onBlur: field.onBlur || noop,
            prefix: field.prefix,
            disabled: field.disabled,
            size: field.size || "default",
            type: "text"
        });
    }
}

export function fieldGenerator(fieldName, field, validateStatus, onChange, selectOnChangeRequire = false) {
    const generatedField = () => {
        const { getFieldDecorator } = this;
        const fieldType = getFieldType(field, getFieldDecorator);
        const fieldComponent = getFieldDecorator(fieldName, {
            rules: field.rules,
            initialValue: field.defaultValue,
            trigger: field.trigger || "onChange",
            onChange: (field.type == "SELECT" && selectOnChangeRequire) ? onChange : undefined,
            validateTrigger: field.validateTrigger || "onChange",
            valuePropName: field.valuePropName || "value"
        })(fieldType);
        return React.createElement(
            FormItem,
            {
                key: fieldName,
                label: field.label,
                validateStatus,
                help: field.helpMessage, 
                hasFeedback: field.hasFeedback,
                className: field.className,
                onChange: onChange,
                ...(field.formItemLayout || BASE_FORM_LAYOUT),
                ...(field.colon ? { colon: field.colon } : {})
            },
            fieldComponent
        );
    };
    generatedField.displayName = "WrapppedFormItem";
    return generatedField;
}

export function createFormData(data) {
    const formData = new FormData();
    Object.keys(data).map(key => formData.append(key, data[key]));
    return formData;
}